import React, { useState, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import { usePrompts, usePromptsViewMode, useTogglePromptViewMode } from "@/lib/prompts-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, LayoutGrid, List, SlidersHorizontal } from "lucide-react";
import { PromptCard } from "@/components/prompts/PromptCard";
import { PromptTable } from "@/components/prompts/PromptTable";
import { PromptModal } from "@/components/prompts/PromptModal";
import { PromptPlayground } from "@/components/prompts/PromptPlayground";
import { motion, AnimatePresence } from "framer-motion";
import type { Prompt } from "@/lib/prompts-store";
export default function PromptsPage() {
  const { t } = useTranslation();
  const prompts = usePrompts();
  const viewMode = usePromptsViewMode();
  const toggleViewMode = useTogglePromptViewMode();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlaygroundOpen, setIsPlaygroundOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | undefined>(undefined);
  const filteredPrompts = useMemo(() => {
    return prompts.filter((p) => {
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                            p.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = statusFilter === "all" || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [prompts, search, statusFilter]);
  const openCreateModal = () => {
    setEditingPrompt(undefined);
    setIsModalOpen(true);
  };
  const openEditModal = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setIsModalOpen(true);
  };
  const openPlayground = () => setIsPlaygroundOpen(true);
  return (
    <AppLayout container>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('prompts_title')}</h1>
            <p className="text-muted-foreground mt-1">Design, test, and manage your enterprise AI prompts.</p>
          </div>
          <Button
            className="gap-2 h-11 px-6 bg-gradient-primary text-primary-foreground shadow-glow hover:opacity-95"
            onClick={openCreateModal}
          >
            <Plus className="w-5 h-5" />
            {t('create_prompt')}
          </Button>
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card/40 backdrop-blur-xl p-4 rounded-xl border border-white/10">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search_prompts')}
              className="pl-10 bg-background/50"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px] bg-background/50">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex border rounded-lg overflow-hidden shrink-0">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={toggleViewMode}
                className="rounded-none h-10 w-10"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={toggleViewMode}
                className="rounded-none h-10 w-10"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="min-h-[400px]">
          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredPrompts.map((prompt) => (
                  <PromptCard key={prompt.id} prompt={prompt} onOpenPlayground={openPlayground} onEdit={openEditModal} />
                ))}
              </motion.div>
            ) : (
              <motion.div key="table" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PromptTable prompts={filteredPrompts} onEdit={openEditModal} onOpenPlayground={() => setIsPlaygroundOpen(true)} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <PromptModal open={isModalOpen} onOpenChange={setIsModalOpen} editPrompt={editingPrompt} />
        <PromptPlayground open={isPlaygroundOpen} onOpenChange={setIsPlaygroundOpen} />
      </div>
    </AppLayout>
  );
}
